import React, {useMemo} from 'react';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {Box, Typography} from '@mui/material';
import {HandoverFooterNavigation} from '../../components/FooterNavigation/HandoverFooterNavigation';
import {HandoverStep} from '../../components/HandoverStep/HandoverStep';
import Loading from '../../components/Loading/Loading';
import {Panel} from '../../components/Panel/Panel';
import {useAccountContext} from '../../contexts/AccountContext';
import {useReviewContext} from '../../contexts/ReviewContext';
import {useVulnerabilityOptions} from '../../features/vulnerabilities/useVulnerabilityOptions';
import {themeColors} from '../../theme/Theme';
import {PostcursorPage} from '../PostcursorPage/PostcursorSummary';
import {ContactVulnerabilitySection, SectionContact} from './ContactVulnerabilitySection';
import {useReviewVulnerabilityDrafts} from './useReviewVulnerabilityDrafts';

const SPP_PROPOSITION_TYPE = 'SPP';
const SECTION_COLOURS = [themeColors.coral, themeColors.green, themeColors.yellow];

/**
 * SFP Vulnerability tube stop (DBO-766): a per-contact table of the
 * vulnerabilities Curo holds, with "Continuing?" and "New Vulnerabilities",
 * saved as review drafts and synced to Curo when the review is submitted.
 */
export const VulnerabilityStepPage: React.FC = () => {
  const {account} = useAccountContext();
  const {reviewDetails, reviewEditable} = useReviewContext();
  const options = useVulnerabilityOptions();
  const contacts = useMemo<SectionContact[]>(
    () =>
      [account?.primaryContact, account?.secondaryContact].flatMap((contact) =>
        contact ? [{id: contact.id, simplifiedName: contact.simplifiedName}] : []
      ),
    [account?.primaryContact, account?.secondaryContact]
  );
  const contactIds = useMemo(() => contacts.map((contact) => contact.id), [contacts]);
  const {registerSection, save, scheduleSave, saving} = useReviewVulnerabilityDrafts(
    reviewDetails?.id,
    contactIds
  );

  if (!account || !reviewDetails || options.loading) {
    return <Loading entityName="vulnerabilities" />;
  }

  return (
    <>
      <Box mt={2} />
      <HandoverStep />
      <Box mt={2} display="flex" flexDirection="column" gap={3}>
        {!options.isEnabled ? (
          <Panel title="Vulnerabilities">
            <Typography>
              Vulnerabilities are managed in Curo for this environment. Nothing to record
              here.
            </Typography>
          </Panel>
        ) : (
          contacts.map((contact, index) => (
            <ContactVulnerabilitySection
              key={contact.id}
              contact={contact}
              reviewId={reviewDetails.id}
              options={options}
              color={SECTION_COLOURS[index % SECTION_COLOURS.length]}
              readOnly={!reviewEditable}
              register={registerSection}
              onChange={scheduleSave}
            />
          ))
        )}
      </Box>
      <HandoverFooterNavigation
        onNext={save}
        onExit={save}
        onNextLoading={saving}
        nextIcon={<TrendingUpIcon />}
      />
    </>
  );
};

/**
 * The "resilience" tube stop route. SFP household accounts get the new
 * vulnerability tables; SPP and trust accounts keep the existing postcursor
 * page, whose wizard still captures their investment philosophy answers.
 */
export const ResilienceStepPage: React.FC = () => {
  const {account} = useAccountContext();
  if (!account) {
    return <Loading entityName="account" />;
  }
  if (account.propositionType === SPP_PROPOSITION_TYPE || account.isTrust) {
    return <PostcursorPage />;
  }
  return <VulnerabilityStepPage />;
};
